import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Loading, PageTitle } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const APLICABLES = ["mensualidad", "inscripcion", "reinscripcion", "combo", "documentacion",
  "pago_anual", "pago_semestral", "cuota", "cuota_diagnostico", "actividad", "uniformes", "libros"];

export default function AdminDescuentos() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nombre: "", tipo: "porcentaje", valor: "", aplicable_a: "mensualidad", recargo: false, activo: true });

  const cargar = () => api.get("/descuentos").then((r) => setRows(r.data));
  useEffect(() => { cargar(); }, []);

  const crear = async () => {
    try {
      await api.post("/descuentos", { ...form, valor: Number(form.valor) });
      toast.success("Regla de descuento creada");
      setOpen(false);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const eliminar = async (id) => {
    await api.delete(`/descuentos/${id}`);
    cargar();
  };

  if (!rows) return <Loading />;

  return (
    <div data-testid="admin-descuentos">
      <PageTitle title="Descuentos y recargos"
                 subtitle="Reglas porcentuales o de monto fijo que restan (descuentos) o suman (recargos) en la factura.">
        <Button className="rounded-full" onClick={() => setOpen(true)} data-testid="nuevo-descuento">
          <Plus className="mr-2 h-4 w-4" /> Nueva regla
        </Button>
      </PageTitle>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="lista-descuentos">
        {rows.map((d) => (
          <article key={d.id} className="lift rounded-lg border border-border/60 bg-card p-6">
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-display text-base font-semibold">{d.nombre}</h3>
              <Badge variant="outline" className="capitalize">{d.aplicable_a}</Badge>
            </div>
            <p className={`mt-4 font-display text-3xl font-extrabold tracking-tight ${
              d.recargo ? "text-destructive" : "text-primary"}`}
               data-testid={`valor-regla-${d.id}`}>
              {d.recargo ? "+" : "−"}{d.tipo === "porcentaje" ? `${d.valor}%` : `RD$${d.valor}`}
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
              <span className="flex flex-wrap gap-2">
                <Badge variant={d.recargo ? "destructive" : "default"}
                       data-testid={`clase-regla-${d.id}`}>
                  {d.recargo ? "recargo" : "descuento"}
                </Badge>
                <Badge variant={d.activo ? "secondary" : "outline"}>{d.activo ? "activa" : "inactiva"}</Badge>
              </span>
              <Button size="icon" variant="ghost" onClick={() => eliminar(d.id)}
                      data-testid={`eliminar-descuento-${d.id}`}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          </article>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="modal-descuento">
          <DialogHeader><DialogTitle className="font-display">
            {form.recargo ? "Nuevo recargo" : "Nueva regla de descuento"}
          </DialogTitle></DialogHeader>
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                     data-testid="descuento-nombre" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <select className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                        value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                        data-testid="descuento-tipo">
                  <option value="porcentaje">Porcentaje</option>
                  <option value="monto">Monto fijo</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>Valor</Label>
                <Input type="number" value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })}
                       data-testid="descuento-valor" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Aplicable a</Label>
              <select className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                      value={form.aplicable_a} onChange={(e) => setForm({ ...form, aplicable_a: e.target.value })}
                      data-testid="descuento-aplicable">
                {APLICABLES.map((a) => <option key={a} value={a}>{a.replace(/_/g, " ")}</option>)}
              </select>
            </div>
            <div className="space-y-3 rounded-md border border-border bg-muted/30 p-4">
              <label className="flex items-start gap-3 text-sm">
                <Checkbox checked={form.recargo} data-testid="descuento-es-recargo"
                          onCheckedChange={(v) => setForm({ ...form, recargo: !!v })} />
                <span>
                  <span className="font-semibold">Es un recargo (suma al total)</span>
                  <span className="block text-xs text-muted-foreground">
                    Por ejemplo un 5% por mora. Si lo deja sin marcar, la regla resta como
                    descuento. El porcentaje del recargo se calcula sobre el monto ya con
                    descuentos.
                  </span>
                </span>
              </label>
            </div>
            <Button className="rounded-full py-6" onClick={crear} data-testid="guardar-descuento">
              {form.recargo ? "Crear recargo" : "Crear descuento"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
